// src/redux/thunks/certConnectThunks.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api/api";
import { OrganizationContactResponse, CertWatchRequest, ApiResponse, ContactSearchResponse, CertWatchResponse, ApproveOrDenyCertWatchResponse, ApproveOrDenyCertWatchRequest } from "../../types/certWatch";
import { setFilter } from "../slices/certConnectSlice";

export const getCertWatchByOrgId = createAsyncThunk<
  ApiResponse<OrganizationContactResponse>,
  CertWatchRequest,
  { rejectValue: string }
>(
  "certConnect/getCertWatchByOrgId",
  async (body, { rejectWithValue }) => {
    try {
      const response = await api.post("certWatch/getCertWatchByOrgId", body);

      if (response.data) {
        const apiResponse: ApiResponse<OrganizationContactResponse> = {
          Data: {
            Items: response.data.Data?.Items || [],
            TotalCount: response.data.Data?.TotalCount || 0,
            PageNumber: response.data.Data?.PageNumber || 1,
            PageSize: response.data.Data?.PageSize || body.PageSize || 10,
          },
          Success: response.data.Success ?? true,
          Message: response.data.Message ?? "Fetched successfully",
          ApiCode: response.data.ApiCode ?? 200,
        };

        return apiResponse;
      } else {
        return rejectWithValue("No data found");
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// new combined thunk
export const setFilterAndFetch = createAsyncThunk<
  ApiResponse<OrganizationContactResponse>,
  Partial<CertWatchRequest>,
  { rejectValue: string; state: any; dispatch: any }
>(
  "certConnect/setFilterAndFetch",
  async (newFilter, { rejectWithValue, dispatch, getState }) => {
    try {
      dispatch(setFilter(newFilter));

      const state = getState();
      const certConnect = state.certConnect;
      const body: CertWatchRequest = {
        ...certConnect.filters,
        page: certConnect.page,
        pageSize: certConnect.pageSize,
        PageNumber:certConnect.page,
        PageSize:certConnect.pageSize,
      };

      const response = await api.post("certWatch/getCertWatchByOrgId", body);

      if (response.data) {
        const apiResponse: ApiResponse<OrganizationContactResponse> = {
          Data: {
            Items: response.data.Data?.Items || [],
            TotalCount: response.data.Data?.TotalCount || 0,
            PageNumber: response.data.Data?.PageNumber || 1,
            PageSize: response.data.Data?.PageSize || body.PageSize || 10,
          },
          Success: response.data.Success ?? true,
          Message: response.data.Message ?? "Fetched successfully",
          ApiCode: response.data.ApiCode ?? 200,
        };

        return apiResponse;
      } else {
        return rejectWithValue("No data found");
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const removeCertWatchRequest = createAsyncThunk<
  boolean, // return type (success/failure)
  { Id: string; ContactId: string }, // argument type
  { rejectValue: string }
>(
  "certConnect/removeCertWatchRequest",
  async ({ Id, ContactId }, { rejectWithValue }) => {
    try {
      const response = await api.post("certWatch/remove", {
        Id,
        ContactId,
      });

      if (!response.data?.Success) {
        return rejectWithValue(response.data?.Message || "Failed to remove");
      }

      return true; // ✅ removed successfully
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const editConnection = createAsyncThunk<
  OrganizationContactResponse, // return type (success/failure)
  { id: string; Available: boolean, Note: string }, // argument type
  { rejectValue: string }
>(
  "certConnect/update",
  async ({ id,Available, Note  }, { rejectWithValue }) => {
    try {
      const response = await api.put("certWatch/update", {
        id,
        Available,
        Note
      });

      if (!response.data?.Success) {
        return rejectWithValue(response.data?.Message || "Failed to remove");
      }

      return response.data.Data as OrganizationContactResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const searchContactbyCCOId = createAsyncThunk<
  ContactSearchResponse,
  { CCOId: string; OrgId: string },
  { rejectValue: string }
>(
  "certConnect/searchContact",
  async ({ CCOId, OrgId }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `contacts/search?CCOId=${CCOId}&OrgId=${OrgId}`
      );

      if (!response.data?.Success) {
        // ✅ Fix: read Message from root, not Data
        return rejectWithValue(response.data?.Message || "Failed");
      }

      return response.data.Data as ContactSearchResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.Message || error.message
      );
    }
  }
);


export const createCertConnect = createAsyncThunk<
  any,
  { ContactId: string; OrganizationId: string },
  { rejectValue: string }
>(
  "certConnect/createCertConnect",
  async ({ ContactId,OrganizationId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `certWatch/create`,{
          ContactId,
          OrganizationId
        }
      );

      if (!response.data?.Success) {
        // ✅ Fix: read Message from root, not Data
        return rejectWithValue(response.data?.Message || "Failed");
      }

      return response.data.Data as any;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.Message || error.message
      );
    }
  }
);

export const getCertWatchByContactId = createAsyncThunk<
  CertWatchResponse[], // return type: array of CertWatchResponse
  void, // request body type
  { rejectValue: string }
>(
  "certWatch/getCertWatchByContactId",
  async (_,{ rejectWithValue }) => {
    try {
      const response = await api.get(
        `certWatch/getCertWatchByContactId`
      );

      //  The array is directly in Data
      if (response.data) {
        return response.data.Data as CertWatchResponse[];
      }
      else{
        console.error(response.data.message);
        return rejectWithValue(response.data.message);

      }

    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const approveOrDenyCertWatch = createAsyncThunk<
  ApproveOrDenyCertWatchResponse,     // return type
  ApproveOrDenyCertWatchRequest,      // request type
  { rejectValue: string }
>(
  "certWatch/approveOrDenyCertWatch",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("certWatch/approve-or-deny", payload);

      if (response.data?.Success) {
        return {
          data: response.data.Data,
          message: response.data.Message,
          success: response.data.Success,
        };
      }

      return rejectWithValue(response.data?.Message || "Action failed");
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.Message || error.message);
    }
  }
);